import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import DOMPurify from 'dompurify';
import { ruleAPI } from '../../utils/api';

const PrivacyPolicy = () => {
    const navigate = useNavigate();
    const [content, setContent] = useState('');
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetchPrivacyPolicy();
    }, []);

    const defaultPrivacyPolicy = `Privacy Policy for Dr Vivara

Dr Vivara ("we", "our", "us") provides a platform that connects users with independent service providers.

Information We Collect:
We may collect personal information such as name, email address, phone number, and user-generated content including messages, audio, and video data.

How We Use Information:
We use this information to provide core app functionality, including account management, communication between users and service providers, and improving user experience.

Data Sharing:
We do not sell personal data. Data may be shared only as necessary to operate the app or comply with legal requirements.

Data Security:
We take reasonable measures to protect user data from unauthorized access or disclosure.

Data Retention Policy:
We retain your personal data only for as long as necessary to fulfill the purposes outlined in this Privacy Policy:
- Account Information: Personal profile data (such as your name, email, phone number, and profile photo) is retained as long as your account remains active. If your account is inactive for a continuous period of 24 months, we will systematically delete or anonymize your personal data.
- Medical & Clinical Records: Telemedicine consultation history, clinical reports, diagnoses, messages with medical practitioners, and electronic prescriptions are clinical medical records. In compliance with national healthcare regulations and legal compliance mandates, these medical records are securely archived and retained for a mandatory minimum period of 3 years from the date of creation before permanent destruction.
- Analytical/Anonymized Data: Non-identifiable or fully anonymized statistical data may be retained indefinitely for analytical and service improvement purposes.

Data Deletion & Account Deletion Policy:
We respect your right to control your personal information. You can request the deletion of your account and associated personal data at any time:
- In-App Deletion Option:
  1. Open the Dr Vivara app and log in to your account.
  2. Go to the "Profile" page from the main navigation.
  3. Under the "Account Settings" section, click "Change Password / Delete Account".
  4. Select the "Delete Account" tab.
  5. Type "DELETE" in the confirmation field to verify, and tap the "Permanently Delete Account" button.
- Email/Support Request:
  - If you are unable to access your account, you can request account and data deletion by sending a formal email to support@drvivara.org with the subject line "Account & Data Deletion Request" from your registered email address.
- Post-Request Processing:
  - Upon receiving your request (either via the app or email), your account will be immediately deactivated and rendered inaccessible.
  - Personal profile data (login credentials, name, email, phone) will be permanently deleted or scrambled/anonymized from our active databases within 30 days.
  - Clinical records, medical histories, and prescription logs are subject to a mandatory 3-year regulatory retention period and will remain securely archived for legal, medical, and administrative compliance. Once this statutory period expires, they will be permanently destroyed.

Contact Us:
Email: support@drvivara.org`;

    const fetchPrivacyPolicy = async () => {
        try {
            // Fetch all active rules and find the privacy_policy
            const res = await ruleAPI.getActive();
            if (res.data?.success) {
                const privacyRule = res.data.data.find(r => r.type === 'privacy_policy');
                if (privacyRule && privacyRule.content.trim() !== '') {
                    setContent(privacyRule.content);
                } else {
                    setContent(defaultPrivacyPolicy);
                }
            } else {
                setContent(defaultPrivacyPolicy);
            }
        } catch (error) {
            console.error('Error fetching privacy policy:', error);
            setContent(defaultPrivacyPolicy);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-dark text-light p-6">
            <div className="max-w-4xl mx-auto">
                <div className="flex items-center mb-8">
                    <button
                        onClick={() => navigate(-1)}
                        className="mr-4 text-gold"
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                        </svg>
                    </button>
                    <h1 className="text-3xl font-bold text-gold">Privacy Policy</h1>
                </div>

                <div className="bg-gray-800 rounded-xl p-8">
                    {loading ? (
                        <div className="flex justify-center items-center h-40">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gold"></div>
                        </div>
                    ) : (
                        <div className="prose prose-invert max-w-none">
                            <div className="text-gray-300 leading-relaxed" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(content) }}></div>
                        </div>
                    )}

                    <div className="mt-8 pt-6 border-t border-gray-700 text-sm text-gray-500">
                        <p>Last updated: {new Date().toLocaleDateString()}</p>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PrivacyPolicy;
